"use client";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * DatePicker Component
 *
 * A date picker component that combines a button trigger with a calendar popover.
 * Supports single date, multiple dates, and date range selection modes.
 * Built on top of react-day-picker with shadcn/ui styling.
 *
 * @example
 * ```tsx
 * // Single date selection
 * <DatePicker
 *   mode="single"
 *   date={date}
 *   onDateChange={setDate}
 *   placeholder="Pick a date"
 * />
 *
 * // Multiple dates selection
 * <DatePicker
 *   mode="multiple"
 *   dates={dates}
 *   onDatesChange={setDates}
 *   placeholder="Pick multiple dates"
 * />
 *
 * // Date range selection
 * <DatePicker
 *   mode="range"
 *   range={range}
 *   onRangeChange={setRange}
 *   placeholder="Pick a date range"
 * />
 * ```
 */
interface DatePickerProps {
    // Common props
    mode?: "single" | "multiple" | "range";
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    fromDate?: Date;
    toDate?: Date;
    maxDates?: number;

    // Single mode props
    date?: Date;
    onDateChange?: (date: Date | undefined) => void;

    // Multiple mode props
    dates?: Date[];
    onDatesChange?: (dates: Date[] | undefined) => void;

    // Range mode props
    range?: { from: Date | undefined; to?: Date | undefined };
    onRangeChange?: (range: { from: Date | undefined; to?: Date | undefined } | undefined) => void;
}

function DatePicker({
    mode = "single",
    placeholder,
    className,
    disabled = false,
    fromDate,
    toDate,
    maxDates,
    date,
    onDateChange,
    dates,
    onDatesChange,
    range,
    onRangeChange,
}: DatePickerProps) {
    // Format display text based on mode and selected values
    const formatDisplayText = () => {
        if (mode === "single") {
            return date ? format(date, "PPP") : placeholder || "Pick a date";
        }

        if (mode === "multiple") {
            if (!dates || dates.length === 0) return placeholder || "Pick multiple dates";
            if (dates.length === 1) return format(dates[0], "PPP");
            if (dates.length === 2) return `${format(dates[0], "PPP")}, ${format(dates[1], "PPP")}`;
            return `${dates.length} dates selected`;
        }

        if (mode === "range") {
            if (!range?.from) return placeholder || "Pick a date range";
            if (!range.to) return format(range.from, "PPP");
            return `${format(range.from, "PPP")} - ${format(range.to, "PPP")}`;
        }

        return placeholder || "Pick a date";
    };

    // Calculate dynamic width based on content
    const getButtonWidth = () => {
        if (mode === "single") {
            return "setu:min-w-[280px] setu:w-[280px]";
        }

        if (mode === "multiple") {
            if (!dates || dates.length === 0) return "setu:min-w-[280px] setu:w-[280px]";
            if (dates.length === 1) return "setu:min-w-[280px] setu:w-[280px]";
            if (dates.length === 2) return "setu:min-w-[280px] setu:w-[400px]";
            return "setu:min-w-[280px] setu:w-[280px]"; // For count display
        }

        if (mode === "range") {
            if (!range?.from) return "setu:min-w-[280px] setu:w-[280px]";
            if (!range.to) return "setu:min-w-[280px] setu:w-[280px]";
            return "setu:min-w-[280px] setu:w-[400px]"; // For range display
        }

        return "setu:min-w-[280px] setu:w-[280px]";
    };

    const disabledFn = (date: Date) => {
        if (fromDate && date < fromDate) return true;
        if (toDate && date > toDate) return true;
        if (mode === "multiple" && maxDates && dates && dates.length >= maxDates) {
            if (!dates.some((d) => d.getTime() === date.getTime())) {
                return true;
            }
        }
        return false;
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        getButtonWidth(),
                        "setu:justify-start setu:text-left setu:font-normal",
                        "setu:text-muted-foreground",
                        className
                    )}
                    disabled={disabled}
                >
                    <CalendarIcon className="setu:mr-2 setu:h-4 setu:w-4" />
                    <span>{formatDisplayText()}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="setu:w-auto setu:p-0">
                {mode === "single" && (
                    <Calendar
                        mode="single"
                        selected={date}
                        onSelect={onDateChange}
                        disabled={disabledFn}
                        initialFocus
                    />
                )}
                {mode === "multiple" && (
                    <Calendar
                        mode="multiple"
                        selected={dates}
                        onSelect={onDatesChange}
                        disabled={disabledFn}
                        initialFocus
                    />
                )}
                {mode === "range" && (
                    <Calendar
                        mode="range"
                        selected={range}
                        onSelect={onRangeChange}
                        disabled={disabledFn}
                        initialFocus
                    />
                )}
            </PopoverContent>
        </Popover>
    );
}

DatePicker.displayName = "DatePicker";

export { DatePicker };
export type { DatePickerProps };
